import React from 'react';
import { SectionHeader } from '../../primitives/SectionHeader';
import { StatCard } from '../../cards/StatCard';
import { QuoteBlock } from '../../cards/QuoteBlock';
import { colors, fonts, space } from '../../tokens';
import { DraggableElement } from '../../../DraggableElement';
import { EnPowerWordmarkInline } from '../../../EnPowerLogo';
import { InlineEditableText } from '../../../InlineEditableText';

const STATS = [
  { value: '100+', label: 'Realisierte Projekte', sub: 'Bundesweit' },
  { value: '70+', label: 'Zufriedene Kunden', sub: 'Gewerbe & Industrie' },
  { value: '10M+', label: 'Investiertes Volumen', sub: 'In €' },
  { value: '10+', label: 'Jahre Expertise', sub: 'PV-Entwicklung' },
];

const REFS = [
  { project: '5 MW Freiflächenanlage', location: 'Hessen', detail: 'Entwicklung & Realisation' },
  { project: '1,2 MW Dachanlage', location: 'NRW', detail: 'über 1 GWh Jahresertrag' },
  { project: '850 kWp Logistikzentrum', location: 'Bayern', detail: 'In Betrieb seit 2014' },
];

/**
 * CompanySection – "Solarenergie ist die Zukunft" auf Page 2.
 * Enthält Eyebrow + Headline + Intro + QuoteBlock + StatsGrid (4 StatCards).
 */
export const CompanySection = ({ content = {}, onTextChange, editMode = false, layoutEditLevel = 'section', dragProps = {} }) => {
  const companyText = {
    header: {
      eyebrowSuffix: content?.header?.eyebrowSuffix || 'Unternehmensprofil',
      headline: content?.header?.headline || 'SOLARENERGIE IST DIE ZUKUNFT',
    },
    intro: {
      paragraph1:
        content?.intro?.paragraph1 ||
        'EN:POWER ist Ihr spezialisierter Partner für die wirtschaftliche Entwicklung von Photovoltaik-Projekten auf Dachflächen. Wir verbinden technische Expertise mit unternehmerischer Erfahrung – für Ergebnisse, die zählen.',
      paragraph2:
        content?.intro?.paragraph2 ||
        'Von der ersten Anfrage bis zum laufenden Betrieb – alles aus einer Hand. Unsere Projekte reichen von Gewerbedachanlagen bis zu Multi-Megawatt-Projekten.',
    },
    quote: {
      quote: content?.quote?.quote || 'Ihr Dach arbeitet für Sie – ohne einen Cent eigener Investition.',
      footnote: content?.quote?.footnote || 'EN:POWER übernimmt alles – Sie kassieren die Pacht.',
    },
  };

  const references = REFS.map((reference, index) => ({
    ...reference,
    location: content?.referenceLocations?.[`ref${index + 1}`] || reference.location,
  }));

  const renderReferences = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: `${space.sm}px` }}>
      {references.map((reference, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: colors.orange, flexShrink: 0 }} />
          <p style={{ fontFamily: fonts.body, fontSize: '9px', color: colors.muted, margin: 0 }}>
            <strong style={{ color: colors.navy }}>{reference.project}</strong>{' • '}
            <InlineEditableText
              as="span"
              value={reference.location}
              editable={editMode}
              onChange={(value) => onTextChange?.(`page2.company.referenceLocations.ref${index + 1}`, value)}
            />
            {' • '}
            <span style={{ color: colors.slateLight }}>{reference.detail}</span>
          </p>
        </div>
      ))}
    </div>
  );

  return (
  <>
    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S2-Unternehmensprofil-Header" {...dragProps} dragAxis="y">
        <div style={{ padding: `${space.lg}px ${space.xxl}px ${space.md}px`, background: 'white', flexShrink: 0 }}>
          <SectionHeader
            eyebrow={(
              <>
                <EnPowerWordmarkInline
                  enColor={colors.navy}
                  powerColor={colors.orange}
                  fontFamily={fonts.body}
                  fontSize="9px"
                  fontWeight={700}
                  letterSpacing="0.14em"
                />
                {' '}
                &bull;{' '}
                <InlineEditableText
                  as="span"
                  value={companyText.header.eyebrowSuffix}
                  editable={editMode}
                  onChange={(value) => onTextChange?.('page2.company.header.eyebrowSuffix', value)}
                />
              </>
            )}
            headline={companyText.header.headline}
          />
        </div>
      </DraggableElement>
    ) : (
      <div style={{ padding: `${space.lg}px ${space.xxl}px ${space.md}px`, background: 'white', flexShrink: 0 }}>
        <SectionHeader
          eyebrow={(
            <>
              <EnPowerWordmarkInline
                enColor={colors.navy}
                powerColor={colors.orange}
                fontFamily={fonts.body}
                fontSize="9px"
                fontWeight={700}
                letterSpacing="0.14em"
              />
              {' '}
              &bull;{' '}
              <InlineEditableText
                as="span"
                value={companyText.header.eyebrowSuffix}
                editable={editMode}
                onChange={(value) => onTextChange?.('page2.company.header.eyebrowSuffix', value)}
              />
            </>
          )}
          headline={companyText.header.headline}
        />
      </div>
    )}

    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S2-Unternehmensprofil-Intro" {...dragProps} dragAxis="y">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 200px',
            gap: `${space.md}px`,
            padding: `0 ${space.xxl}px ${space.md}px`,
            background: 'white',
            flexShrink: 0,
            minHeight: '200px',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <p className="proposal-body" style={{ fontSize: '10.5px', marginBottom: '9px' }}>
                <InlineEditableText
                  as="span"
                  value={companyText.intro.paragraph1}
                  editable={editMode}
                  multiline
                  onChange={(value) => onTextChange?.('page2.company.intro.paragraph1', value)}
                />
              </p>
              <p className="proposal-body" style={{ fontSize: '10.5px' }}>
                <InlineEditableText
                  as="span"
                  value={companyText.intro.paragraph2}
                  editable={editMode}
                  multiline
                  onChange={(value) => onTextChange?.('page2.company.intro.paragraph2', value)}
                />
              </p>
            </div>
            {renderReferences()}
          </div>
          <QuoteBlock
            quote={companyText.quote.quote}
            footnote={companyText.quote.footnote}
          />
        </div>
      </DraggableElement>
    ) : (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 200px',
          gap: `${space.md}px`,
          padding: `0 ${space.xxl}px ${space.md}px`,
          background: 'white',
          flexShrink: 0,
          minHeight: '200px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <p className="proposal-body" style={{ fontSize: '10.5px', marginBottom: '9px' }}>
              <InlineEditableText
                as="span"
                value={companyText.intro.paragraph1}
                editable={editMode}
                multiline
                onChange={(value) => onTextChange?.('page2.company.intro.paragraph1', value)}
              />
            </p>
            <p className="proposal-body" style={{ fontSize: '10.5px' }}>
              <InlineEditableText
                as="span"
                value={companyText.intro.paragraph2}
                editable={editMode}
                multiline
                onChange={(value) => onTextChange?.('page2.company.intro.paragraph2', value)}
              />
            </p>
          </div>
          {renderReferences()}
        </div>
        <QuoteBlock
          quote={companyText.quote.quote}
          footnote={companyText.quote.footnote}
        />
      </div>
    )}

    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S2-Stats-Band" {...dragProps} dragAxis="y">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            background: colors.bgSoft,
            borderTop: `1px solid ${colors.border}`,
            borderBottom: `1px solid ${colors.border}`,
            flexShrink: 0,
            minHeight: '128px',
            padding: `${space.md}px 0`,
            boxSizing: 'border-box',
          }}
        >
          {STATS.map((s, i) => (
            <DraggableElement key={i} id={`S2-Stat-${i + 1}`} {...dragProps} dragAxis="y">
              <StatCard value={s.value} label={s.label} sub={s.sub} withRightBorder={i < STATS.length - 1} />
            </DraggableElement>
          ))}
        </div>
      </DraggableElement>
    ) : (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          background: colors.bgSoft,
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
          minHeight: '128px',
          padding: `${space.md}px 0`,
          boxSizing: 'border-box',
        }}
      >
        {STATS.map((s, i) => (
          <StatCard key={i} value={s.value} label={s.label} sub={s.sub} withRightBorder={i < STATS.length - 1} />
        ))}
      </div>
    )}

    <div style={{ height: '1px', background: colors.border, flexShrink: 0 }} />
  </>
  );
};
